/**
 * SCRUM-151 (P-1) step 13 — Zod schemas for the Property baseline pricing HTTP boundary.
 *
 * SetBaselineRequestSchema  — single-property PATCH body (§6 spec endpoint #1).
 * BulkSetBaselineRequestSchema — POST bulk body (§6 spec endpoint #2).
 *
 * Both schemas use the existing LocalDateSchema from @artemis/validators/time to
 * validate ISO-8601 calendar dates (YYYY-MM-DD) including leap-year correctness.
 *
 * Moved 2026-05-28 (SCRUM-158): pricing-baseline.ts → pricing/baseline.ts to
 * group the pricing Zod schemas under one subfolder (mirrors identity/ and
 * property/ subfolder conventions). Re-export path through @artemis/validators
 * is unchanged for consumers.
 *
 * Adversarial tests: packages/validators/src/__tests__/pricing-baseline.legacy.spec.ts
 */
import { z } from 'zod';
import { LocalDateSchema } from '../time/index.js';
// ---------------------------------------------------------------------------
// Reusable date-string validator
// ---------------------------------------------------------------------------
const isoLocalDateString = z
    .string()
    .refine((s) => LocalDateSchema.safeParse(s).success, {
    message: 'must be a valid ISO-8601 calendar date (YYYY-MM-DD)',
});
// ---------------------------------------------------------------------------
// SetBaselineRequest
// ---------------------------------------------------------------------------
/**
 * The raw object shape without the at-least-one-field invariant.
 * Exported so `BulkSetBaselineRequestSchema` can call `.omit()` on it.
 */
const SetBaselineFieldsShape = z.object({
    weekdayBasePriceCents: z.number().int().nonnegative().optional(),
    weekendBasePriceCents: z.number().int().nonnegative().optional(),
    weekdayFloorRateCents: z.number().int().nonnegative().optional(),
    weekendFloorRateCents: z.number().int().nonnegative().optional(),
    effectiveStart: isoLocalDateString.optional(),
});
/** Refinement: at least one pricing field must be present in the request. */
function atLeastOneField(req) {
    return (req.weekdayBasePriceCents !== undefined ||
        req.weekendBasePriceCents !== undefined ||
        req.weekdayFloorRateCents !== undefined ||
        req.weekendFloorRateCents !== undefined);
}
export const SetBaselineRequestSchema = SetBaselineFieldsShape.refine(atLeastOneField, { message: 'at least one baseline field must be provided' });
// ---------------------------------------------------------------------------
// BulkSetBaselineRequest
// ---------------------------------------------------------------------------
/**
 * The `change` sub-object omits `effectiveStart` (that lives at the batch
 * level so all properties in the bulk share a single schedule date).
 */
const BulkChangeShape = SetBaselineFieldsShape.omit({ effectiveStart: true });
const BulkChangeSchema = BulkChangeShape.refine(atLeastOneField, {
    message: 'at least one baseline field must be provided',
});
export const BulkSetBaselineRequestSchema = z.object({
    propertyIds: z.array(z.string().uuid()).min(1).max(500),
    change: BulkChangeSchema,
    effectiveStart: isoLocalDateString.optional(),
});
//# sourceMappingURL=baseline.js.map